/**
 * Uygulama kökü. Depo açılışının üç sonucu ayrı ayrı tasarlıdır:
 *  - açılıyor       → yükleme ekranı
 *  - kilitli        → LockedScreen (LOCKED_BY_OTHER_TAB)
 *  - sunucu yok     → ServerUnavailableScreen (SERVER_UNAVAILABLE)
 *  - kilitli defter → önce TanitimSayfasi (KURULUM_GEREKLI, KILITLI; 10.10.2026
 *                     kullanıcı kararı: ilk girişte tanıtım), "Defter" kartından
 *                     KurulumEkrani / KilitEkrani; DefterKayipEkrani (DEFTER_KAYIP)
 *  - diğer hatalar  → hata ekranı + yeniden dene
 */

import { defterDegisti } from "./lib/seriOnbellegi";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { LocalRepository, RepositoryInfo } from "@parafon/contracts/local-api";
import { AppShell } from "./components/AppShell";
import { DilSecici } from "./components/DilSecici";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LockedScreen } from "./components/LockedScreen";
import { ServerUnavailableScreen } from "./components/ServerUnavailableScreen";
import { DefterKayipEkrani } from "./components/kilit/DefterKayipEkrani";
import { KilitEkrani } from "./components/kilit/KilitEkrani";
import { IceAktarScreen } from "./screens/IceAktarScreen";
import { KurulumEkrani } from "./components/kilit/KurulumEkrani";
import { OtomatikKilit, yeniProfilIstendiMi } from "./components/kilit/OtomatikKilit";
import { ThemeProvider } from "./components/ThemeProvider";
import { TanitimSayfasi } from "./components/tanitim/TanitimSayfasi";
import { CentralProvider } from "./central/CentralContext";
import type { CentralContextValue } from "./central/CentralContext";
import { createCentral } from "./central/createCentral";
import type { CentralBundle } from "./central/createCentral";
import { EsitlemeSaglayici } from "./central/EsitlemeSaglayici";
import { asRpcError, isLockedByOtherTab, isServerUnavailable } from "./lib/rpcError";
import { routeHref, useRoute } from "./lib/router";
import { bekleyenPaylasimVar, paylasimiYakala } from "./lib/sanalPaylasim";
import { acilisAdresi, acilisHedefiniBul } from "./lib/acilisSayfasi";
import { REPOSITORY_IS_FAKE, createDataLayer } from "./repo/createRepository";
import type { DataLayer } from "./repo/createRepository";
import { RepositoryProvider, useRepositoryContext } from "./repo/RepositoryContext";
import type { RepositoryContextValue } from "./repo/RepositoryContext";
import { createBellekKasa } from "./yedek/kasa";
import type { YedekKasasi } from "./yedek/kasa";
import { YedekSaglayici } from "./yedek/YedekSaglayici";
import { AccountsScreen } from "./screens/AccountsScreen";
import { AnaSayfaScreen } from "./screens/AnaSayfaScreen";
import { ManualPricesScreen } from "./screens/ManualPricesScreen";
import { ManualValuationsScreen } from "./screens/ManualValuationsScreen";
import { PositionsScreen } from "./screens/PositionsScreen";
import { ReportScreen } from "./screens/ReportScreen";
import { GrafikScreen } from "./screens/GrafikScreen";
import { DonemselAnalizScreen } from "./screens/DonemselAnalizScreen";
import { SanalHesaplarScreen } from "./screens/SanalHesaplarScreen";
import { SettingsScreen } from "./screens/SettingsScreen";
import { TrackingScreen } from "./screens/TrackingScreen";
import { TransactionsScreen } from "./screens/TransactionsScreen";
import { Button, ErrorBlock, Spinner } from "./ui/primitives";
import { KabiliyetSaglayici } from "./kabiliyet/KabiliyetSaglayici";
import { M } from "./dil";

export interface AppProps {
  /** Testler kendi sahtesini geçirir; üretimde `createDataLayer()` kullanılır. */
  readonly repository?: LocalRepository;
  readonly isFake?: boolean;
  /**
   * Aşama 2 eklentileri. Testler ve `?sahte=1` bunları enjekte eder; verilmezse
   * merkez YOK sayılır (uygulama çalışır, arama devre dışı kalır ve sebebi
   * ekranda görünür).
   */
  readonly dataLayer?: DataLayer;
  readonly centralBundle?: CentralBundle;
  /**
   * Y şeridi: otomatik yedeğin kasası. Enjekte edilmiş koşumda (testler)
   * verilmezse BELLEK kasası — testler tarayıcının IndexedDB'sine yazmasın.
   */
  readonly yedekKasasi?: YedekKasasi;
}

type OpenState =
  | { readonly status: "opening" }
  | { readonly status: "open"; readonly info: RepositoryInfo }
  | { readonly status: "failed"; readonly error: unknown };

/** Enjekte edilmiş bir koşumda merkez yoktur ve bu ARIZA DEĞİLDİR. */
const NO_CENTRAL: CentralBundle = {
  central: null,
  sync: null,
  baseUrl: null,
  isFake: false,
  unavailable: M.kabuk.merkez.kosumdaYok,
};

export function App({
  repository,
  isFake = REPOSITORY_IS_FAKE,
  dataLayer,
  centralBundle,
  yedekKasasi,
}: AppProps = {}) {
  // Depo, önbellek ve merkez TEK kez kurulur. `repository`/`dataLayer`
  // verildiyse (testler, gömülü kullanım) ağ tarafı hiç kurulmaz: merkezi
  // enjekte edilmemiş bir koşumda dinamik modül yüklemeye çalışmak, testleri
  // sessizce ağa bağlamak olurdu.
  const injected = repository !== undefined || dataLayer !== undefined;

  const layer = useMemo<DataLayer>(() => {
    if (dataLayer !== undefined) return dataLayer;
    if (repository !== undefined) {
      return {
        repo: repository,
        prices: null,
        cacheUnavailable: M.kabuk.merkez.kosumdaOnbellekYok,
      };
    }
    return createDataLayer();
  }, [dataLayer, repository]);

  const bundle = useMemo<CentralBundle>(
    () => centralBundle ?? (injected ? NO_CENTRAL : createCentral()),
    [centralBundle, injected],
  );

  const kasa = useMemo<YedekKasasi | undefined>(
    () => yedekKasasi ?? (injected ? createBellekKasa() : undefined),
    [yedekKasasi, injected],
  );

  // Sanal hesap paylaşım linki: kasa kilitliyken de geldiyse kural belleğe
  // alınır, kilit açılınca Sanal Hesaplar'da onay sorulur (`lib/sanalPaylasim.ts`).
  useState(() => paylasimiYakala());

  const repo = layer.repo;
  const [openState, setOpenState] = useState<OpenState>({ status: "opening" });
  const [attempt, setAttempt] = useState(0);
  const [revision, setRevision] = useState(0);
  // ÇOKLU PROFİL: kilit ekranında "Yeni profil ekle" ya da Ayarlar'dan gelen istek.
  const [yeniProfil, setYeniProfil] = useState(() => yeniProfilIstendiMi());
  // Tanıtım sayfasındaki "Defter" kartına basıldı mı (kurulum / kilit ekranı).
  const [defterAdimi, setDefterAdimi] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOpenState({ status: "opening" });
    repo
      .open()
      .then((info) => {
        if (!cancelled) setOpenState({ status: "open", info });
      })
      .catch((error: unknown) => {
        if (!cancelled) setOpenState({ status: "failed", error });
      });
    return () => {
      cancelled = true;
    };
  }, [repo, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  // Kasa parolayla açıldı / kuruldu / getirildi: ilk sayfa kasanın durumuna
  // göre seçilecek (`lib/acilisSayfasi.ts`). Kısa süreli oturumla yeniden
  // yükleme kilit ekranından geçmez, bu bayrak hiç kalkmaz.
  const [acilis, setAcilis] = useState(false);
  const acildi = useCallback(() => {
    setAcilis(true);
    setAttempt((a) => a + 1);
  }, []);
  const acilisSecildi = useCallback(() => setAcilis(false), []);
  // Defter değişti: grafik önbelleği BÜTÜNÜYLE gider (`lib/seriOnbellegi.ts`).
  const invalidate = useCallback(() => {
    defterDegisti();
    setRevision((r) => r + 1);
  }, []);
  // Eşitleme bitti: ekranlar yenilenir ama grafik önbelleği SİLİNMEZ — neyin
  // değiştiğini eşitleme sağlayıcısı `fiyatlarDegisti` ile kendisi söyler.
  const fiyatlarYenilendi = useCallback(() => setRevision((r) => r + 1), []);

  if (openState.status === "opening") {
    return (
      <ThemeProvider>
        <main className="flex min-h-dvh items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <p className="text-[15px] font-semibold text-ink">Parafon</p>
            <Spinner label={M.kabuk.acilis.vtAciliyor} />
          </div>
        </main>
      </ThemeProvider>
    );
  }

  if (openState.status === "failed") {
    const kod = asRpcError(openState.error)?.code;
    const tanitimdaMi = !defterAdimi && !yeniProfil && (kod === "KURULUM_GEREKLI" || kod === "KILITLI");
    // Üst satır: tanıtıma dönüş (varsa) ve dil seçici (kullanıcı kararı
    // 28.10.2026 — dil kasa açılmadan önce de değiştirilebilir).
    const geriVar = !yeniProfil && (kod === "KURULUM_GEREKLI" || kod === "KILITLI");
    const ustSatir = (
      <div className="mx-auto flex max-w-xl items-center justify-between gap-3 px-6 pt-4 text-[13px]">
        {geriVar ? (
          <button type="button" className="text-accent hover:underline" onClick={() => setDefterAdimi(false)}>
            {M.kabuk.acilis.tanitimaDon}
          </button>
        ) : (
          <span />
        )}
        <DilSecici />
      </div>
    );
    return (
      <ThemeProvider>
        {!tanitimdaMi && ustSatir}
        {tanitimdaMi ? (
          <TanitimSayfasi durum={kod === "KILITLI" ? "kilitli" : "kurulum"} onDefter={() => setDefterAdimi(true)} />
        ) : kod === "KURULUM_GEREKLI" ? (
          <KurulumEkrani repo={repo} onBitti={acildi} />
        ) : kod === "KILITLI" && yeniProfil ? (
          <KurulumEkrani
            repo={repo}
            onBitti={() => {
              setYeniProfil(false);
              acildi();
            }}
            yeniProfil={{ onVazgec: () => setYeniProfil(false) }}
          />
        ) : kod === "KILITLI" ? (
          <>
            {/* "Yeni profil ekle" kilit ekranında YOK (gelir modeli, kullanıcı kararı
                26.10.2026): ek kasa hakkı kasanın içinde durur, kilitliyken
                bilinemez. Ayarlar › Kasa kilidi'nden, hakkı olan kasa açıkken. */}
            <KilitEkrani repo={repo} onAcildi={acildi} />
          </>
        ) : kod === "DEFTER_KAYIP" ? (
          <DefterKayipEkrani repo={repo} error={openState.error} onDevam={retry} />
        ) : isLockedByOtherTab(openState.error) ? (
          <LockedScreen onRetry={retry} />
        ) : isServerUnavailable(openState.error) ? (
          <ServerUnavailableScreen error={openState.error} onRetry={retry} />
        ) : (
          <main className="mx-auto max-w-xl p-6">
            <h1 className="px-4 text-[16px] font-semibold text-ink">{M.kabuk.acilis.baslatilamadi}</h1>
            <ErrorBlock error={openState.error} onRetry={retry} title={M.kabuk.acilis.vtAcilamadi} />
            <p className="px-4 text-[13px] text-muted">
              {M.kabuk.acilis.dokunulmadi}
            </p>
            <div className="px-4 pt-3">
              <Button onClick={() => globalThis.location?.reload()}>{M.kabuk.acilis.sayfayiYenile}</Button>
            </div>
          </main>
        )}
      </ThemeProvider>
    );
  }

  const contextValue: RepositoryContextValue = {
    repo,
    info: openState.info,
    isFake,
    revision,
    invalidate,
  };

  // Merkez ve önbellek AYRI bağlamda: `RepositoryContext` Aşama 1'de donan
  // sözleşmeyi taşıyor, Aşama 2'nin yeteneği onun YANINDA duruyor. `revision`
  // ikisinde de paylaşılıyor ki eşitleme sonrası hem defter hem durum
  // listeleri tek `invalidate()` ile yenilensin.
  const centralValue: CentralContextValue = {
    central: bundle.central,
    prices: layer.prices,
    sync: bundle.sync,
    isFake: bundle.isFake,
    unavailable: bundle.unavailable,
    cacheUnavailable: layer.cacheUnavailable,
    revision,
    invalidate: fiyatlarYenilendi,
  };

  return (
    <ThemeProvider>
      <RepositoryProvider value={contextValue}>
        {/* Kilitli defter: süre dolunca bekleyen yazım + yeniden yükleme. */}
        <OtomatikKilit />
        <CentralProvider value={centralValue}>
          {/* P-a: açılışta ve sekme öne gelince merkezden çeker. Depo açık
              olmadan buraya gelinmiyor — sıra bu yüzden doğru. */}
          <EsitlemeSaglayici>
            {/* Y: defter değişince şifreli bulut yedeği. */}
            <YedekSaglayici {...(kasa === undefined ? {} : { kasa })}>
              {/* Gelir modeli: kasadaki sertifikalarla kapıdan oturum + jeton. */}
              <KabiliyetSaglayici>
                <ErrorBoundary>
                  <Routes acilis={acilis} onAcilisSecildi={acilisSecildi} />
                </ErrorBoundary>
              </KabiliyetSaglayici>
            </YedekSaglayici>
          </EsitlemeSaglayici>
        </CentralProvider>
      </RepositoryProvider>
    </ThemeProvider>
  );
}

function Routes({ acilis, onAcilisSecildi }: { readonly acilis: boolean; readonly onAcilisSecildi: () => void }) {
  const [route] = useRoute();
  const { repo } = useRepositoryContext();

  useEffect(() => {
    if (!acilis) return undefined;
    // Paylaşılan bir kural onay bekliyorsa açılış sayfası kuralından önce o gelir.
    if (bekleyenPaylasimVar()) {
      if (globalThis.location) globalThis.location.hash = routeHref("sanal-hesaplar");
      onAcilisSecildi();
      return undefined;
    }
    let iptal = false;
    // Okunamazsa bulunulan sayfa kalır: açılış sayfası bir kolaylık, engel değil.
    void acilisHedefiniBul(repo)
      .then((h) => {
        if (!iptal && globalThis.location) globalThis.location.hash = acilisAdresi(h);
      }, () => undefined)
      .finally(() => {
        if (!iptal) onAcilisSecildi();
      });
    return () => {
      iptal = true;
    };
  }, [acilis, repo, onAcilisSecildi]);

  useEffect(() => {
    if (globalThis.document) globalThis.document.title = `Parafon · ${route}`;
  }, [route]);

  if (acilis) {
    return (
      <main className="flex min-h-dvh items-center justify-center">
        <Spinner label={M.kabuk.acilis.kasaAciliyor} />
      </main>
    );
  }

  return (
    <AppShell route={route}>
      {route === "ana-sayfa" && <AnaSayfaScreen />}
      {route === "hesaplar" && <AccountsScreen />}
      {route === "hareketler" && <TransactionsScreen />}
      {route === "ice-aktar" && <IceAktarScreen />}
      {route === "pozisyonlar" && <PositionsScreen />}
      {route === "rapor" && <ReportScreen />}
      {route === "grafik" && <GrafikScreen />}
      {route === "donemsel-analiz" && <DonemselAnalizScreen />}
      {route === "sanal-hesaplar" && <SanalHesaplarScreen />}
      {route === "izleme" && <TrackingScreen />}
      {route === "fiyatlar" && <ManualPricesScreen />}
      {route === "degerleme" && <ManualValuationsScreen />}
      {route === "ayarlar" && <SettingsScreen />}
    </AppShell>
  );
}
